/**
 * ============================================================================
 * StepsNodeView — NodeView React do bloco `steps` (lista de passos estilo
 *                 GitBook). Renderiza <ol class="kb-steps"> via NodeViewContent
 *                 e sobrepõe botões "+" entre passos (e nas bordas) para
 *                 inserir um novo `stepItem` NA POSIÇÃO exata onde o usuário
 *                 quer, com renumeração automática (CSS counter).
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial (inserção por posição + hover
 *            "+" GitBook-like).
 * ============================================================================
 */
import { NodeViewWrapper, NodeViewContent, type NodeViewProps } from "@tiptap/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";

// Preserva o scroll externo do editor antes de rodar comandos que chamem focus.
function preserveScroll(view: any, fn: () => void) {
  const el = view.dom.closest("[data-kb-scroll]") as HTMLElement | null;
  const top = el?.scrollTop ?? 0;
  const left = el?.scrollLeft ?? 0;
  fn();
  const apply = () => {
    if (!el) return;
    el.scrollTop = top;
    el.scrollLeft = left;
  };
  apply();
  requestAnimationFrame(() => {
    apply();
    requestAnimationFrame(apply);
  });
}

interface StepBoundary {
  /** Posição no documento onde inserir o novo passo. */
  insertAt: number;
  /** Coordenada Y (relativa ao wrapper) para posicionar o botão. */
  top: number;
}

export function StepsNodeView({ editor, node, getPos }: NodeViewProps) {
  const editable = editor?.isEditable ?? true;
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [boundaries, setBoundaries] = useState<StepBoundary[]>([]);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  // Recalcula geometria dos gaps entre passos com base no DOM real.
  const recompute = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const ol = wrap.querySelector<HTMLOListElement>('ol[data-type="steps"]');
    if (!ol) {
      setBoundaries([]);
      return;
    }
    const items = Array.from(ol.querySelectorAll<HTMLLIElement>(':scope > li[data-type="step-item"]'));
    if (items.length === 0) {
      setBoundaries([]);
      return;
    }


    const basePos = typeof getPos === "function" ? getPos() : 0;
    if (basePos == null) {
      setBoundaries([]);
      return;
    }

    const wrapRect = wrap.getBoundingClientRect();
    const list: StepBoundary[] = [];

    // Fronteira ANTES do primeiro item = basePos + 1 (dentro do steps)
    const firstRect = items[0].getBoundingClientRect();
    list.push({ insertAt: basePos + 1, top: firstRect.top - wrapRect.top - 6 });

    // Cada item filho contribui com sua fronteira DEPOIS = anterior + child.nodeSize
    let cursor = basePos + 1;
    for (let i = 0; i < node.childCount; i++) {
      cursor += node.child(i).nodeSize;
      const nextItem = items[i + 1];
      const rect = nextItem
        ? nextItem.getBoundingClientRect()
        : items[i].getBoundingClientRect();
      const top = nextItem
        ? rect.top - wrapRect.top - 6
        : rect.bottom - wrapRect.top - 6;
      list.push({ insertAt: cursor, top });
    }

    setBoundaries(list);
  }, [getPos, node]);

  useEffect(() => {
    recompute();
    const wrap = wrapRef.current;
    if (!wrap) return;
    const obs = new MutationObserver(() => recompute());
    obs.observe(wrap, { childList: true, subtree: true, characterData: true });
    const onResize = () => recompute();
    window.addEventListener("resize", onResize);
    return () => {
      obs.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [recompute]);

  const insertStepAt = (insertAt: number) => {
    if (!editable) return;
    preserveScroll(editor.view, () => {
      editor
        .chain()
        .insertContentAt(insertAt, {
          type: "stepItem",
          content: [{ type: "paragraph" }],
        })
        // Entra dentro do parágrafo do novo passo (+2 = passa stepItem + paragraph open tags)
        .setTextSelection(insertAt + 2)
        .focus()
        .run();
    });
  };

  return (
    <NodeViewWrapper as="div" className="kb-steps-wrap" data-type="steps-wrap">
      <div
        ref={wrapRef}
        className="kb-steps-hover-area"
        onMouseLeave={() => setActiveIdx(null)}
      >
        <NodeViewContent
          as={"ol" as any}
          className="kb-steps"
          data-type="steps"
        />


        {editable &&
          boundaries.map((b, idx) => (
            <button
              key={`${b.insertAt}-${idx}`}
              type="button"
              contentEditable={false}
              className="kb-step-insert-between"
              data-kb-editor-ui="true"
              title="Inserir passo aqui"
              style={{ top: b.top }}
              onMouseEnter={() => setActiveIdx(idx)}
              onMouseLeave={() => setActiveIdx(null)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                insertStepAt(b.insertAt);
              }}
              data-active={activeIdx === idx ? "true" : "false"}
              aria-label="Inserir passo nesta posição"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          ))}
      </div>
    </NodeViewWrapper>
  );
}
